import React from "react";
import { Stack, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import {
    CompetitiveRoundSummaries,
    CompetitiveRoundSummariesPlayerSnapshotsInner,
} from "../../reckon-api";
import AgentIcon from "../../components/Icons/AgentIcon";
import WeaponIcon from "../../components/Icons/WeaponIcon";
import AbilityIcon, { ShieldIcon } from "../../components/Icons/AbilityIcon";
import { CreditsIcon } from "../../components/Icons/GameIcons";
import { attacker_color, defender_color } from "../../theme";

const ABILITY_SLOTS = ["ULTIMATE", "ABILITY_1", "ABILITY_2", "GRENADE_ABILITY"] as const;
const ICON_SX = { width: 16, height: 16, minWidth: 16, minHeight: 16, objectFit: "contain" };

function inventoryItems(state?: CompetitiveRoundSummariesPlayerSnapshotsInner) {
    const inventory = state?.inventory as any;
    if (!inventory) return [] as Array<{ name?: string; slot?: string }>;
    return Array.isArray(inventory) ? inventory : Object.values(inventory);
}

function getAbility(state: CompetitiveRoundSummariesPlayerSnapshotsInner | undefined, slot: string) {
    const abilities = state?.abilities as any;
    if (!abilities) return null;
    if (Array.isArray(abilities)) {
        return abilities.find((ability: any) => ability.slot === slot) ?? null;
    }
    return abilities[slot] ?? Object.values(abilities).find((ability: any) => ability?.slot === slot) ?? null;
}

function getWeapon(state: CompetitiveRoundSummariesPlayerSnapshotsInner | undefined, summary?: CompetitiveRoundSummaries) {
    const equipped = state?.alive_state?.equipped_item?.name;
    if (equipped && !/melee|knife/i.test(equipped)) return equipped;
    const primary = inventoryItems(state).find((item) => /primary/i.test(item.slot || ""));
    return primary?.name || equipped || summary?.main_weapon || undefined;
}

function getShield(state: CompetitiveRoundSummariesPlayerSnapshotsInner | undefined, summary?: CompetitiveRoundSummaries) {
    const armorItem = inventoryItems(state).find((item) =>
        /armor|shield/i.test(`${item.slot} ${item.name}`)
    );
    if (armorItem?.name) return armorItem.name;
    if (summary?.shield) return summary.shield;
    const armor = state?.alive_state?.armor ?? 0;
    if (armor >= 50) return "Heavy Shields";
    if (armor > 0) return "Light Shields";
    return undefined;
}

function hasSpike(state: CompetitiveRoundSummariesPlayerSnapshotsInner | undefined) {
    return inventoryItems(state).some((item) => /spike/i.test(`${item.slot} ${item.name}`));
}

function hasCharges(state: CompetitiveRoundSummariesPlayerSnapshotsInner | undefined, slot: string) {
    const ability = getAbility(state, slot);
    if (!ability) return false;
    if (slot === "ULTIMATE") return ability.base_charges === ability.max_charges;
    return (ability.base_charges ?? 0) > 0;
}

function teamAlive(players: CompetitiveRoundSummaries[], timestamp: number) {
    return players.filter((player) => (player?.player_snapshots?.[timestamp]?.alive_state?.health ?? 0) > 0).length;
}

function VitalBars(props: { health: number; armor: number; align: "left" | "right"; color: string }) {
    const health = Math.max(0, Math.min(100, props.health));
    const armor = Math.max(0, Math.min(50, props.armor));
    const reverse = props.align === "left";
    return (
        <Stack direction="row" alignItems="center" spacing={0.45} sx={{ width: "100%", flexDirection: reverse ? "row-reverse" : "row" }}>
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    height: 8,
                    borderRadius: "3px",
                    background: "rgba(255,255,255,0.08)",
                    overflow: "hidden",
                    position: "relative",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        left: reverse ? "auto" : 0,
                        right: reverse ? 0 : "auto",
                        bottom: 0,
                        width: `${health}%`,
                        height: 5,
                        background: props.color,
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        left: reverse ? "auto" : 0,
                        right: reverse ? 0 : "auto",
                        top: 0,
                        width: `${(armor / 50) * 100}%`,
                        height: 3,
                        background: "linear-gradient(90deg, #8ecfff 0%, #e8f4ff 100%)",
                    }}
                />
            </Box>
            <Typography
                sx={{
                    fontSize: "0.58rem",
                    fontWeight: 800,
                    fontFamily: "monospace",
                    color: "rgba(255,255,255,0.85)",
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                }}
            >
                {health}
                <Box component="span" sx={{ color: "rgba(180,220,255,0.9)", ml: 0.4 }}>
                    {armor}
                </Box>
            </Typography>
        </Stack>
    );
}

export function TeamScoreboard(props: {
    timestamp: number;
    players: CompetitiveRoundSummaries[];
    align: "left" | "right";
}) {
    const side = props.players[0]?.side;
    const alive = teamAlive(props.players, props.timestamp);
    const sideColor = side === "Attacker" ? attacker_color : defender_color;

    return (
        <Box
            sx={{
                width: 220,
                minWidth: 220,
                flexShrink: 0,
                height: "auto",
                display: "flex",
                flexDirection: "column",
                background: `linear-gradient(${props.align === "left" ? "270deg" : "90deg"}, ${sideColor}28 0%, rgba(8,10,16,0.92) 55%)`,
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: "8px",
                overflow: "hidden",
                backdropFilter: "blur(10px)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    px: 1,
                    py: 0.45,
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                    flexShrink: 0,
                }}
            >
                <Typography
                    sx={{
                        fontSize: "0.65rem",
                        fontWeight: 800,
                        letterSpacing: "0.08em",
                        color: sideColor,
                        textTransform: "uppercase",
                    }}
                >
                    {side === "Attacker" ? "ATK" : side === "Defender" ? "DEF" : "TEAM"}
                </Typography>
                <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, fontFamily: "monospace", color: "#fff" }}>
                    {props.players.length ? alive : "–"}
                </Typography>
            </Stack>
            <Stack sx={{ flexShrink: 0 }}>
                {Array.from({ length: 5 }).map((_, index) => (
                    <SidePlayerRow
                        key={`${props.align}-${index}`}
                        summary={props.players[index]}
                        timestamp={props.timestamp}
                        align={props.align}
                    />
                ))}
            </Stack>
        </Box>
    );
}

function SidePlayerRow(props: {
    summary?: CompetitiveRoundSummaries;
    timestamp: number;
    align: "left" | "right";
}) {
    const state = props.summary?.player_snapshots?.[props.timestamp];
    const isAlive = !state || (state.alive_state?.health ?? 0) > 0;
    const health = state?.alive_state?.health ?? 0;
    const armor = state?.alive_state?.armor ?? 0;
    const sideColor = props.summary?.side === "Attacker" ? attacker_color : defender_color;
    const weapon = getWeapon(state, props.summary);
    const shield = getShield(state, props.summary);
    const spike = hasSpike(state);

    const agent = props.summary?.agent ? (
        <AgentIcon agent_name={props.summary.agent} sx={{ width: 26, height: 26, minWidth: 26, minHeight: 26 }} />
    ) : (
        <Box sx={{ width: 26, height: 26, flexShrink: 0 }} />
    );

    const name = (
        <Typography
            sx={{
                flex: 1,
                minWidth: 0,
                fontSize: "0.7rem",
                fontWeight: 700,
                color: "rgba(255,255,255,0.95)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textAlign: props.align === "left" ? "right" : "left",
            }}
        >
            {props.summary?.player || ""}
        </Typography>
    );

    const kda = (
        <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, fontFamily: "monospace", color: "rgba(255,255,255,0.7)", flexShrink: 0 }}>
            {state ? `${state.kills ?? 0}/${state.deaths ?? 0}/${state.assists ?? 0}` : ""}
        </Typography>
    );

    const identity =
        props.align === "left" ? (
            <>
                {kda}
                {name}
                {agent}
            </>
        ) : (
            <>
                {agent}
                {name}
                {kda}
            </>
        );

    const gearItems = [
        weapon ? <WeaponIcon key="w" weapon={weapon} width={40} height={16} reverse={props.align === "left"} /> : <Box key="w" sx={{ width: 40, height: 16 }} />,
        shield ? <ShieldIcon key="s" shield={shield} sx={ICON_SX} /> : <Box key="s" sx={{ width: 16, height: 16 }} />,
        ...(props.summary?.agent
            ? ABILITY_SLOTS.map((slot) => (
                  <AbilityIcon
                      key={slot}
                      agent_name={props.summary?.agent || ""}
                      slot={slot}
                      abilityState={getAbility(state, slot) ?? undefined}
                      sx={{ ...ICON_SX, filter: `brightness(${hasCharges(state, slot) ? 100 : 28}%)` }}
                  />
              ))
            : []),
        <Box key="spike" sx={{ width: 14, height: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {spike ? (
                <Box
                    component="img"
                    src="https://res.cloudinary.com/xenesis/image/upload/c_scale,h_16,w_16/v1/valAssets/Spike.png"
                    alt="Spike"
                    sx={{ width: 14, height: 14, objectFit: "contain" }}
                />
            ) : null}
        </Box>,
        <Stack key="money" direction="row" alignItems="center" spacing={0.3} sx={{ flexShrink: 0 }}>
            <CreditsIcon title="Money" color="e8c547" />
            <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, fontFamily: "monospace", color: "#e8c547" }}>
                {state ? state.money ?? 0 : ""}
            </Typography>
        </Stack>,
    ];

    const gear = props.align === "left" ? [...gearItems].reverse() : gearItems;

    return (
        <Stack
            sx={{
                px: 0.75,
                py: 0.4,
                gap: 0.25,
                minWidth: 0,
                opacity: props.summary && !isAlive ? 0.38 : 1,
                filter: props.summary && !isAlive ? "grayscale(0.6)" : "none",
                borderBottom: "1px solid rgba(255,255,255,0.05)",
                background: props.summary ? `${sideColor}14` : "transparent",
            }}
        >
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                {identity}
            </Stack>
            {props.summary ? <VitalBars health={isAlive ? health : 0} armor={isAlive ? armor : 0} align={props.align} color={sideColor} /> : null}
            <Stack
                direction="row"
                alignItems="center"
                spacing={0.35}
                sx={{ minWidth: 0, justifyContent: props.align === "left" ? "flex-end" : "flex-start" }}
            >
                {gear}
            </Stack>
        </Stack>
    );
}
